'use client';

import * as React from 'react';

import { cn, useWindowDimensions } from '@heidi/utils';
import { useEffect, useState } from 'react';

import { useDialogDrawerRegistry } from '../contexts';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../drawer';

interface BaseProps {
  children: React.ReactNode;
}

const DialogDrawerModeContext = React.createContext<boolean | null>(null);

/**
 * Parts must agree with their root on dialog-vs-drawer mode within a single
 * render — independent useWindowDimensions() instances can tear during a
 * resize across the breakpoint, making Radix throw "must be used within".
 */
const useIsDesktopMode = (): boolean => {
  const fromRoot = React.useContext(DialogDrawerModeContext);
  const { isDesktop } = useWindowDimensions();
  return fromRoot ?? isDesktop;
};

interface RootDialogDrawerProps extends BaseProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onOpenChangeComplete?: (open: boolean) => void;
  dismissible?: boolean;
  modal?: boolean;
  /** Controls Vaul Drawer input repositioning on mobile (iOS has bugs when true). Defaults to false. */
  drawerRepositionInputs?: boolean;
}

interface DialogDrawerProps extends BaseProps {
  id?: string;
  className?: string;
  asChild?: true;
  hideCloseIcon?: boolean;
  overlayClassName?: string;
  dismissible?: boolean;
  onCloseIconClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onOpenAutoFocus?: ((event: Event) => void) | undefined;
  onCloseAutoFocus?: ((event: Event) => void) | undefined;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  hideOverlay?: boolean;
  disableDrag?: boolean;
  closeIconClassName?: string;
  /** When set, portal dialog/drawer into this element. */
  portalContainer?: HTMLElement | null;
  /**
   * When true, prevents the dialog from closing when focus moves outside.
   * Useful for non-modal dialogs where focus can shift due to other UI elements
   * (e.g., Select dropdowns closing), but clicking outside should still dismiss.
   */
  preventFocusOutsideClose?: boolean;
}

const DialogDrawer = ({
  dismissible = true,
  children,
  modal = true,
  drawerRepositionInputs = false,
  ...props
}: RootDialogDrawerProps) => {
  const { isDesktop } = useWindowDimensions();
  const [id, setId] = useState<string>();
  const { register, unregister, trackOpen } = useDialogDrawerRegistry();
  const open = props.open;

  useEffect(() => {
    const registerId = register();
    setId(registerId);
    return () => {
      unregister(registerId);
    };
  }, [register, unregister]);

  useEffect(() => {
    if (id && open !== undefined) {
      trackOpen(id, open);
    }
  }, [id, open, trackOpen]);

  return (
    <DialogDrawerModeContext.Provider value={isDesktop}>
      {isDesktop ? (
        <Dialog modal={modal} {...props}>
          {children}
        </Dialog>
      ) : (
        <Drawer
          dismissible={dismissible}
          modal={modal}
          repositionInputs={drawerRepositionInputs}
          {...props}
        >
          {children}
        </Drawer>
      )}
    </DialogDrawerModeContext.Provider>
  );
};

const DialogDrawerTrigger = ({ className, children, ...props }: DialogDrawerProps) => {
  const isDesktop = useIsDesktopMode();
  const DialogDrawerTrigger = isDesktop ? DialogTrigger : DrawerTrigger;

  return (
    <DialogDrawerTrigger className={className} {...props}>
      {children}
    </DialogDrawerTrigger>
  );
};

const DialogDrawerClose = ({ className, children, ...props }: DialogDrawerProps) => {
  const isDesktop = useIsDesktopMode();
  const DialogDrawerClose = isDesktop ? DialogClose : DrawerClose;

  return (
    <DialogDrawerClose className={className} {...props}>
      {children}
    </DialogDrawerClose>
  );
};

const DialogDrawerContent = ({
  className,
  dismissible = true,
  hideCloseIcon = false,
  children,
  onCloseIconClick,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  hideOverlay = false,
  disableDrag = false,
  closeIconClassName,
  portalContainer,
  preventFocusOutsideClose = false,
  ...props
}: DialogDrawerProps) => {
  const isDesktop = useIsDesktopMode();

  if (isDesktop) {
    return (
      <DialogContent
        className={className}
        hideCloseIcon={!dismissible || hideCloseIcon}
        dismissible={dismissible}
        preventFocusOutsideClose={preventFocusOutsideClose}
        closeIconClassName={closeIconClassName}
        onInteractOutside={(e) => {
          if (!dismissible) {
            e.preventDefault();
          }
        }}
        onCloseIconClick={onCloseIconClick}
        onOpenAutoFocus={onOpenAutoFocus}
        onCloseAutoFocus={onCloseAutoFocus}
        onEscapeKeyDown={(e) => {
          if (!dismissible) {
            e.preventDefault();
          }
          onEscapeKeyDown?.(e);
        }}
        portalContainer={portalContainer}
        {...props}
      >
        {children}
      </DialogContent>
    );
  }
  return (
    <DrawerContent
      className={className}
      hideOverlay={hideOverlay}
      data-vaul-no-drag={disableDrag}
      hideCloseIcon={hideCloseIcon}
      closeIconClassName={closeIconClassName}
      portalContainer={portalContainer}
      onEscapeKeyDown={(e) => {
        if (!dismissible) {
          e.preventDefault();
        }
        onEscapeKeyDown?.(e);
      }}
      {...props}
    >
      {children}
    </DrawerContent>
  );
};

const DialogDrawerDescription = ({
  className,
  children,
  ...props
}: DialogDrawerProps) => {
  const isDesktop = useIsDesktopMode();
  const DialogDrawerDescription = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <DialogDrawerDescription className={className} {...props}>
      {children}
    </DialogDrawerDescription>
  );
};

const DialogDrawerHeader = ({ className, children, ...props }: DialogDrawerProps) => {
  const isDesktop = useIsDesktopMode();
  const DialogDrawerHeader = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <DialogDrawerHeader className={cn('px-0', className)} {...props}>
      {children}
    </DialogDrawerHeader>
  );
};

const DialogDrawerTitle = ({ className, children, ...props }: DialogDrawerProps) => {
  const isDesktop = useIsDesktopMode();
  const DialogDrawerTitle = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <DialogDrawerTitle className={className} {...props}>
      {children}
    </DialogDrawerTitle>
  );
};

const DialogDrawerBody = ({ className, children, ...props }: DialogDrawerProps) => {
  return (
    <div className={cn('px-0', className)} {...props}>
      {children}
    </div>
  );
};

const DialogDrawerFooter = ({ className, children, ...props }: DialogDrawerProps) => {
  const isDesktop = useIsDesktopMode();
  const DialogDrawerFooter = isDesktop ? DialogFooter : DrawerFooter;

  return (
    <DialogDrawerFooter className={cn('px-0', className)} {...props}>
      {children}
    </DialogDrawerFooter>
  );
};

export {
  DialogDrawer,
  DialogDrawerBody,
  DialogDrawerClose,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerFooter,
  DialogDrawerHeader,
  DialogDrawerTitle,
  DialogDrawerTrigger,
};
